import {Box, Button} from '@primer/react'
import type {ReactNode} from 'react'
import {NestableActionMenu} from '@github-ui/nestable-action-menu'

export type SharedPickerProps = {
  leadingIconElement?: ReactNode
  label: string
  anchorProps?: React.HTMLAttributes<HTMLElement>
  sharedPickerMainValue: ReactNode
  ariaLabel?: string
  readonly?: boolean
  size?: 'small' | 'large'
  /**
   * Whether to render the picker as a nested menu item (true) versus a standalone button (false; default).
   */
  nested?: boolean
}

export const SharedPicker = ({
  leadingIconElement,
  label,
  anchorProps,
  sharedPickerMainValue,
  ariaLabel,
  readonly,
  size,
  nested = false,
}: SharedPickerProps) => {
  const sharedProps = {'aria-label': ariaLabel, 'aria-disabled': readonly, disabled: readonly}

  if (nested) {
    return (
      <NestableActionMenu.MenuItemAnchor leadingVisual={leadingIconElement} {...anchorProps} {...sharedProps}>
        {label}
      </NestableActionMenu.MenuItemAnchor>
    )
  }

  return (
    <Button size={size ? size : 'small'} {...anchorProps} {...sharedProps}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <Box sx={{color: 'fg.muted', fontWeight: 'normal'}}>{label}</Box>
        <Box
          sx={{
            display: leadingIconElement ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            '> *:not(:last-child)': {
              mr: '-3px',
            },
          }}
        >
          {leadingIconElement}
        </Box>
        <Box sx={{fontWeight: 'bold'}}>{sharedPickerMainValue}</Box>
      </Box>
    </Button>
  )
}

try{ SharedPicker.displayName ||= 'SharedPicker' } catch {}